import { Input } from '@lensora/ui'
import { Send } from 'lucide-react'
import * as React from 'react'

interface ComposerBarProps {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  placeholder: string
  disabled: boolean
  autoFocusToken: string
}

// Persistent, always-rendered quick-entry bar that sits below the transcript.
// It routes free text to whatever the currently active turn expects — see
// OnboardingChat's submit handlers. Kept visually secondary: chips/buttons
// inside each turn remain the primary UI, this is a supplementary shortcut.
export function ComposerBar({ value, onChange, onSubmit, placeholder, disabled, autoFocusToken }: ComposerBarProps) {
  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (!disabled) inputRef.current?.focus()
  }, [autoFocusToken, disabled])

  function handleSubmit() {
    if (disabled || !value.trim()) return
    onSubmit()
  }

  return (
    <div className="border-t border-border bg-surface px-1 pt-3">
      <div className="flex items-center gap-2">
        <Input
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleSubmit()
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          aria-label="Quick reply"
          className="text-sm"
        />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={disabled || !value.trim()}
          aria-label="Send"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border text-text-muted transition-colors hover:enabled:bg-surface-overlay hover:enabled:text-text disabled:opacity-40"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
